/**
 * User Settings V2 — admin-mode header strip rendered above the form:
 * role chips for the target user plus an Impersonate action.
 *
 * Contains:
 * - AdminUserHeader (default export)
 */

import { Button, Chip, Stack } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useUser } from '@sophocles/app';
import StatusChip from '@sophocles/components/StatusChip';
import { impersonateUser } from '@sophocles/features/auth/authActions';
import tokenService from '@sophocles/services/token';
import { determineIsAccounting } from '@sophocles/utils/userPermissionUtils';

import { BASIC_AUTH_GROUPS } from './authGroupConstants';

// Header strip rendered under the TitleBar in admin mode.  Combines:
// - role chips for the target user (Superuser / Staff / Reseller /
//   Admin / Manager / 3rd-party Admin / Deleted or Inactive).  Reads from the
//   loaded settings, not the in-flight Formik values, so chips
//   reflect saved state.
// - an Impersonate action — gated to staff/super requesters and
//   excluded for self / accounting targets, matching the legacy
//   EditUserPage rules.
export default function AdminUserHeader({ targetUserId, permissions }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user: authUser, isStaff, isSuperUser: isSuper } = useUser();
  const [impersonating, setImpersonating] = useState(false);

  const groups = permissions.groups || [];
  const groupIds = groups.map(g => g.id);
  const chips = [];
  if (permissions.is_superuser) {
    chips.push({ label: t('userSettings.isSuperuser', 'Superuser'), color: 'error' });
  }
  if (permissions.is_staff) {
    chips.push({ label: t('userSettings.isStaff', 'Staff'), color: 'warning' });
  }
  if (groupIds.includes(BASIC_AUTH_GROUPS.RESELLER)) {
    chips.push({ label: t('userSettings.reseller', 'Reseller'), color: 'primary' });
  } else if (groupIds.includes(BASIC_AUTH_GROUPS.ADMIN)) {
    chips.push({ label: t('userSettings.administrator', 'Administrator'), color: 'primary' });
  } else if (groupIds.includes(BASIC_AUTH_GROUPS.MANAGER)) {
    chips.push({ label: t('userSettings.siteGroupManager', 'Manager'), color: 'primary' });
  } else if (groupIds.includes(BASIC_AUTH_GROUPS.THIRD_PARTY_ADMIN)) {
    chips.push({ label: t('userSettings.thirdPartyAdmin', '3rd-party Admin'), color: 'secondary' });
  }

  // Impersonate gating — same rules as the legacy edit-user page.
  // tokenService.isImpersonating is read at render time; if the
  // requester is already impersonating someone else we hide the
  // button (their session is the impersonated identity, so chaining
  // would produce a confusing nested state).
  const targetIsSuper = !!permissions.is_superuser;
  const targetIsAccounting = determineIsAccounting(groupIds);
  const isAlreadyImpersonating = !!tokenService.isImpersonating;
  const canImpersonate =
    !isAlreadyImpersonating &&
    targetUserId &&
    targetUserId !== authUser?.get?.('id') &&
    (!targetIsAccounting || isSuper) &&
    ((isStaff && !targetIsSuper) || isSuper);

  const handleImpersonate = () => {
    if (impersonating) return;
    setImpersonating(true);
    dispatch(impersonateUser(targetUserId))
      .then(() => {
        // Full reload swaps in the impersonated session at every
        // layer (Redux store, axios interceptors, Cognito token
        // service) — same approach the legacy page used.
        window.location.reload();
      })
      .catch(() => setImpersonating(false));
  };

  const isDeleted = !!permissions.is_deleted;
  const isInactive = !isDeleted && !permissions.is_active;

  if (chips.length === 0 && !isDeleted && !isInactive && !canImpersonate) return null;
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      useFlexGap
      spacing={1}
      sx={{ mt: 1, mb: 2 }}
    >
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {chips.map(c => (
          <Chip key={c.label} label={c.label} color={c.color} size="small" />
        ))}
        {isDeleted ? <StatusChip label={t('general.deleted', 'Deleted')} color="bad" /> : null}
        {isInactive ? <StatusChip label={t('userSettings.inactive', 'Inactive')} color="neutral" /> : null}
      </Stack>
      {canImpersonate ? (
        <Button variant="outlined" size="small" onClick={handleImpersonate} disabled={impersonating}>
          {impersonating
            ? t('userSettings.impersonating', 'Impersonating…')
            : t('userSettings.impersonate', 'Impersonate')}
        </Button>
      ) : null}
    </Stack>
  );
}
